import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { TutorAuthProvider } from "./context/TutorAuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import TutorDeletedOverlay from "./components/tutor/TutorDeletedOverlay";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import ProtectedTutorRoute from "./components/ProtectedTutorRoute";
import GlobalSiteScripts from "./components/GlobalSiteScripts";
import OrganizationKnowledgeGraph from "./components/OrganizationKnowledgeGraph";
import { useLocation } from "react-router-dom";

// V157 — Route-aware wrapper. Suppresses the marketing JSON-LD graph +
// GA/tag-manager scripts on secure portal pages so the private surface
// never carries public SEO/analytics payloads.
function SiteChromeOnPublicRoutes() {
    const { pathname } = useLocation();
    if (pathname.startsWith("/portal/")) return null;
    // V173.2 — Public invoice must stay chrome-free so Print → PDF captures
    // only the invoice itself (fixes the 4-page sitemap-leak bug).
    if (pathname.startsWith("/invoice/") || pathname.startsWith("/fee-quote/")) return null;
    return (
        <>
            <GlobalSiteScripts />
            <OrganizationKnowledgeGraph />
        </>
    );
}
import AnalyticsListener from "./components/AnalyticsListener";
import ErrorBoundary from "./components/ErrorBoundary";

import Home from "./pages/Home";
import About from "./pages/About";
import Founder from "./pages/Founder";
import Team from "./pages/Team";
import HowItWorks from "./pages/HowItWorks";
import Courses from "./pages/Courses";
import BoardCoursePage from "./pages/BoardCoursePage";
import AuthorityLanding from "./pages/AuthorityLanding";
import { AUTHORITY_PAGE_SLUGS } from "./data/authorityPagesData";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import BlogCategory from "./pages/BlogCategory";
import FAQ from "./pages/FAQ";
import PayNow from "./pages/PayNow";
import PublicPaymentLink from "./pages/PublicPaymentLink";
import PublicInvoice from "./pages/PublicInvoice";
import PublicFeeQuote from "./pages/PublicFeeQuote";
import Contact from "./pages/Contact";
import Register from "./pages/Register";
import BecomeATutor from "./pages/BecomeATutor";
import TutorGuide from "./pages/TutorGuide";
import Pricing from "./pages/Pricing";
import TimeConverterPage from "./pages/TimeConverterPage";
import HtmlSitemap from "./pages/HtmlSitemap";
import CityLanding from "./pages/CityLanding";
import CityTopicLanding from "./pages/CityTopicLanding";
import { SEO_TOPICS, SEO_CITIES } from "./data/cityTopicData";
import OnlineCity from "./pages/OnlineCity";
import { TIER2_CITY_SLUGS } from "./data/tier2Cities";
import { TIER3_BATCH_2A_SLUGS } from "./data/tier3CitiesBatch2A";
import InternationalCity from "./pages/InternationalCity";
import { INTERNATIONAL_CITY_SLUGS } from "./data/internationalCitiesData";
import { INTERNATIONAL_CITIES_BATCH_2B_SLUGS } from "./data/internationalCitiesBatch2B";
import OnlineCountry from "./pages/OnlineCountry";
import OnlineTuitionWorldwide from "./pages/OnlineTuitionWorldwide";
import BoardSubjectCity from "./pages/BoardSubjectCity";
import { PROG_BOARDS, PROG_SUBJECTS, PROG_CITIES } from "./data/programmaticSEO";
import HomeTuitionLocality from "./pages/HomeTuitionLocality";
import { OFFLINE_LOCALITIES } from "./data/offlineLocalityData";
import { StatesHubPage, CitiesHubPage } from "./pages/IndiaHubs";
import LeaveReview from "./pages/LeaveReview";
import BecomeTutorFAQs from "./pages/BecomeTutorFAQs";
import TutorJobLanding from "./pages/TutorJobLanding";
import { TUTOR_JOB_SLUGS } from "./data/tutorJobsData";
import Policy from "./pages/Policy";
import AdminLogin from "./pages/AdminLogin";
import AdminForgotPassword from "./pages/AdminForgotPassword";
import AdminResetPassword from "./pages/AdminResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import PortalPage from "./pages/PortalPage";
import LiveClassroom from "./pages/LiveClassroom";
// V75 — Phase 5 Continuous SEO Framework
import SeoAuditDashboard from "./pages/admin/SeoAuditDashboard";
import LiveClassroomSetup from "./pages/admin/LiveClassroomSetup";
// V77 — 1-on-1 Live Online Makeover Classes (additive service)
import MakeoverLanding from "./pages/MakeoverLanding";
import MakeoverJabalpurOffline from "./pages/MakeoverJabalpurOffline";
import MakeoverLeadsPanel from "./pages/admin/MakeoverLeadsPanel";
import TutorLogin from "./pages/TutorLogin";
import TutorDashboard from "./pages/TutorDashboard";
import TutorProfile from "./pages/TutorProfile";
import TutorLeads from "./pages/TutorLeads";
import TutorDemos from "./pages/TutorDemos";
import TutorTuitions from "./pages/TutorTuitions";
import TutorAttendance from "./pages/TutorAttendance";
import TutorCalendar from "./pages/TutorCalendar";
import { TutorNotifications, TutorSupport, TutorEarnings } from "./pages/TutorMisc";
import TutorRefer from "./pages/TutorRefer";
import TutorMe from "./pages/TutorMe";
import TutorFAQs from "./pages/TutorFAQs";
import TutorPortalUnavailable from "./pages/TutorPortalUnavailable";
import NotFound from "./pages/NotFound";

// V61 Phase 2 — Wrapper for the 1,440 programmatic Board × Subject × City pages.
// Slug pattern: /{board}-{subject}-tutor-{city}
// Falls through to <NotFound /> for anything else.
function ProgrammaticBoardSubjectCityRoute() {
    // Read the slug from URL — useParams from react-router-dom
    const slug = window.location.pathname.replace(/^\/+/, "").split("?")[0].split("#")[0];
    // Try to match against all board-subject-tutor-city permutations.
    // Longest-first matching for boards + subjects + cities so multi-word slugs
    // (e.g. "state-board", "computer-science", "south-korea") resolve correctly.
    const boardKeys = Object.keys(PROG_BOARDS).sort((a, b) => b.length - a.length);
    const subjectKeys = Object.keys(PROG_SUBJECTS).sort((a, b) => b.length - a.length);
    const cityKeys = Object.keys(PROG_CITIES).sort((a, b) => b.length - a.length);
    for (const b of boardKeys) {
        if (!slug.startsWith(`${b}-`)) continue;
        const rest1 = slug.slice(b.length + 1); // after "board-"
        for (const s of subjectKeys) {
            if (!rest1.startsWith(`${s}-tutor-`)) continue;
            const cityPart = rest1.slice(s.length + "-tutor-".length);
            if (cityKeys.includes(cityPart)) {
                return <BoardSubjectCity boardSlug={b} subjectSlug={s} citySlug={cityPart} />;
            }
        }
    }
    return <NotFound />;
}


function App() {
    return (
        <ErrorBoundary>
        <div className="App">
            <HelmetProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <TutorAuthProvider>
                        <SettingsProvider>
                            <SiteChromeOnPublicRoutes />
                            <ScrollToTop />
                            <AnalyticsListener />
                            <Toaster richColors position="top-right" />
                            <TutorDeletedOverlay />
                            <Routes>
                        {/* V157 — Secure parent/tutor portals: rendered
                            standalone, OUTSIDE the marketing Layout. Prevents
                            the public site header/footer/CTAs/SEO graph from
                            leaking into a private per-student surface. */}
                        <Route path="/portal/parent/:token" element={<PortalPage party="parent" />} />
                        <Route path="/portal/tutor/:token" element={<PortalPage party="tutor" />} />
                        {/* V158 — Native in-platform live classroom (100ms + tldraw). */}
                        <Route path="/portal/:party/:token/session/:schedule_id/live" element={<LiveClassroom />} />
                        {/* V173.2 — Public invoice is STANDALONE (no marketing Layout).
                            Fixes the 4-page PDF bug: the <Layout /> wrapper was
                            emitting the website header + sticky CTAs + footer +
                            sitemap into the print DOM, which the browser then
                            paginated into 3 extra pages. Rendering the invoice
                            outside Layout means Print → Save-as-PDF captures
                            only the invoice itself. */}
                        <Route path="/invoice/:token" element={<PublicInvoice />} />
                        <Route path="/fee-quote/:token" element={<PublicFeeQuote />} />

                        {/* Public site (with shared layout) */}
                        <Route element={<Layout />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/founder" element={<Founder />} />
                            <Route path="/team" element={<Team />} />
                            <Route path="/how-it-works" element={<HowItWorks />} />
                            <Route path="/courses" element={<Courses />} />
                            <Route path="/courses/:boardSlug" element={<BoardCoursePage />} />
                            {/* /online-classes and /foreign-students are now auto-generated by AUTHORITY_PAGE_SLUGS below */}
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/category/:slug" element={<BlogCategory />} />
                            <Route path="/blog/:slug" element={<BlogDetail />} />
                            {/* Short shareable alias — same component, easy to share on WhatsApp / Status */}
                            <Route path="/post/:slug" element={<BlogDetail />} />
                            <Route path="/faqs" element={<FAQ />} />
                            <Route path="/pay-now" element={<PayNow />} />
                            <Route path="/pay/:linkId" element={<PublicPaymentLink />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/become-a-tutor" element={<BecomeATutor />} />
                            <Route path="/tutor-guide" element={<TutorGuide />} />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/time-converter" element={<TimeConverterPage />} />
                            <Route path="/sitemap" element={<HtmlSitemap />} />
                            <Route path="/policies/:policy" element={<Policy />} />
                            <Route path="/jabalpur" element={<CityLanding citySlug="jabalpur" />} />
                            <Route path="/bhopal" element={<CityLanding citySlug="bhopal" />} />
                            <Route path="/nagpur" element={<CityLanding citySlug="nagpur" />} />
                            <Route path="/indore" element={<OnlineCity citySlug="indore" />} />

                            {/* Keyword-slug alias routes (canonical → /<city>) — same component, same content,
                                canonical points to the short slug so no cannibalisation. These exist so we
                                show up for "home tuition in Bhopal" / "home tutors in Bhopal" / "online
                                classes in Bhopal" / "Anand Tutorials Bhopal" style searches with a perfectly
                                keyword-matching URL. Identical wiring for Jabalpur and Nagpur. */}
                            <Route path="/home-tuition-jabalpur" element={<CityLanding citySlug="jabalpur" aliasPath="/home-tuition-jabalpur" />} />
                            <Route path="/home-tutors-jabalpur" element={<CityLanding citySlug="jabalpur" aliasPath="/home-tutors-jabalpur" />} />
                            <Route path="/online-classes-jabalpur" element={<CityLanding citySlug="jabalpur" aliasPath="/online-classes-jabalpur" />} />
                            <Route path="/anand-tutorials-jabalpur" element={<CityLanding citySlug="jabalpur" aliasPath="/anand-tutorials-jabalpur" />} />

                            <Route path="/home-tuition-bhopal" element={<CityLanding citySlug="bhopal" aliasPath="/home-tuition-bhopal" />} />
                            <Route path="/home-tutors-bhopal" element={<CityLanding citySlug="bhopal" aliasPath="/home-tutors-bhopal" />} />
                            <Route path="/online-classes-bhopal" element={<CityLanding citySlug="bhopal" aliasPath="/online-classes-bhopal" />} />
                            <Route path="/anand-tutorials-bhopal" element={<CityLanding citySlug="bhopal" aliasPath="/anand-tutorials-bhopal" />} />

                            <Route path="/home-tuition-nagpur" element={<CityLanding citySlug="nagpur" aliasPath="/home-tuition-nagpur" />} />
                            <Route path="/home-tutors-nagpur" element={<CityLanding citySlug="nagpur" aliasPath="/home-tutors-nagpur" />} />
                            <Route path="/online-classes-nagpur" element={<CityLanding citySlug="nagpur" aliasPath="/online-classes-nagpur" />} />
                            <Route path="/anand-tutorials-nagpur" element={<CityLanding citySlug="nagpur" aliasPath="/anand-tutorials-nagpur" />} />

                            {/* Programmatic City × Topic SEO landing pages */}
                            {Object.entries(SEO_TOPICS).flatMap(([topicSlug, topic]) =>
                                SEO_CITIES.map((citySlug) => (
                                    <Route
                                        key={`${topicSlug}-${citySlug}`}
                                        path={`/${topicSlug}-${topic.urlVerb}-${citySlug}`}
                                        element={<CityTopicLanding topicSlug={topicSlug} citySlug={citySlug} />}
                                    />
                                ))
                            )}

                            {/* National authority landing pages — high-intent SEO */}
                            {AUTHORITY_PAGE_SLUGS.map((slug) => (
                                <Route
                                    key={slug}
                                    path={`/${slug}`}
                                    element={<AuthorityLanding slug={slug} />}
                                />
                            ))}

                            {/* V7 India SEO hubs — State directory + City directory */}
                            <Route path="/online-tuition-india-states" element={<StatesHubPage />} />
                            <Route path="/online-tuition-india-cities" element={<CitiesHubPage />} />

                            {/* V10e — Public review submission */}
                            <Route path="/leave-review" element={<LeaveReview />} />
                            <Route path="/leave-review/:cityParam" element={<LeaveReview />} />

                            {/* Become-a-tutor FAQ hub */}
                            <Route path="/become-a-tutor-faqs" element={<BecomeTutorFAQs />} />

                            <Route path="/home-tuition-mumbai" element={<OnlineCity citySlug="mumbai" />} />
                            <Route path="/home-tuition-delhi" element={<OnlineCity citySlug="delhi" />} />
                            <Route path="/home-tuition-bangalore" element={<OnlineCity citySlug="bangalore" />} />
                            <Route path="/home-tuition-pune" element={<OnlineCity citySlug="pune" />} />
                            <Route path="/home-tuition-hyderabad" element={<OnlineCity citySlug="hyderabad" />} />
                            <Route path="/home-tuition-chennai" element={<OnlineCity citySlug="chennai" />} />
                            <Route path="/home-tuition-kolkata" element={<OnlineCity citySlug="kolkata" />} />
                            <Route path="/home-tuition-ahmedabad" element={<OnlineCity citySlug="ahmedabad" />} />
                            <Route path="/home-tuition-indore" element={<OnlineCity citySlug="indore" />} />

                            {/* Strong online-keyword aliases that rank for "online tuition in <city>"
                                searches — same OnlineCity component, same content. Routes auto-canonicalise
                                back to /home-tuition-<city> so no cannibalisation. */}
                            <Route path="/online-tuition-mumbai" element={<OnlineCity citySlug="mumbai" />} />
                            <Route path="/online-tuition-delhi" element={<OnlineCity citySlug="delhi" />} />
                            <Route path="/online-tuition-bangalore" element={<OnlineCity citySlug="bangalore" />} />
                            <Route path="/online-tuition-pune" element={<OnlineCity citySlug="pune" />} />
                            <Route path="/online-tuition-hyderabad" element={<OnlineCity citySlug="hyderabad" />} />
                            <Route path="/online-tuition-chennai" element={<OnlineCity citySlug="chennai" />} />
                            <Route path="/online-tuition-kolkata" element={<OnlineCity citySlug="kolkata" />} />
                            <Route path="/online-tuition-ahmedabad" element={<OnlineCity citySlug="ahmedabad" />} />
                            <Route path="/online-tuition-indore" element={<OnlineCity citySlug="indore" />} />
                            <Route path="/online-tuition-delhi-ncr" element={<OnlineCity citySlug="delhi" />} />

                            {/* V94 — Phase 1: 33 Tier-2 Indian city landing pages for 1:1 Live
                                Online Classes. Additive; every existing city route above is untouched.
                                Content lives in /data/tier2Cities.js. */}
                            {TIER2_CITY_SLUGS.map((slug) => (
                                <Route
                                    key={`t2-${slug}`}
                                    path={`/online-tuition-${slug}`}
                                    element={<OnlineCity citySlug={slug} />}
                                />
                            ))}

                            {/* Wave 2 · Batch 2A (Feb-2026) — 11 India Tier-3 city pages. */}
                            {TIER3_BATCH_2A_SLUGS.map((slug) => (
                                <Route
                                    key={`t3-${slug}`}
                                    path={`/online-tuition-${slug}`}
                                    element={<OnlineCity citySlug={slug} />}
                                />
                            ))}

                            {/* Wave 1 (Feb-2026) — 10 international cities (USA/UK/UAE/CA/AU) with country-code URLs. */}
                            {INTERNATIONAL_CITY_SLUGS.map((slug) => (
                                <Route
                                    key={`intl-${slug}`}
                                    path={`/online-tuition-${slug}`}
                                    element={<InternationalCity citySlug={slug} />}
                                />
                            ))}

                            {/* Wave 2 · Batch 2B (Feb-2026) — 14 international city pages (SA/QA/KW/OM/DE/IE/FR/NZ). */}
                            {INTERNATIONAL_CITIES_BATCH_2B_SLUGS.map((slug) => (
                                <Route
                                    key={`intl-2b-${slug}`}
                                    path={`/online-tuition-${slug}`}
                                    element={<InternationalCity citySlug={slug} />}
                                />
                            ))}

                            <Route path="/online-tuition-usa" element={<OnlineCountry countrySlug="usa" />} />
                            <Route path="/online-tuition-uk" element={<OnlineCountry countrySlug="uk" />} />
                            <Route path="/online-tuition-australia" element={<OnlineCountry countrySlug="australia" />} />
                            <Route path="/online-tuition-canada" element={<OnlineCountry countrySlug="canada" />} />
                            <Route path="/online-tuition-uae" element={<OnlineCountry countrySlug="uae" />} />
                            <Route path="/online-tuition-singapore" element={<OnlineCountry countrySlug="singapore" />} />
                            <Route path="/online-tuition-saudi-arabia" element={<OnlineCountry countrySlug="saudi-arabia" />} />
                            <Route path="/online-tuition-germany" element={<OnlineCountry countrySlug="germany" />} />
                            <Route path="/online-tuition-new-zealand" element={<OnlineCountry countrySlug="new-zealand" />} />
                            <Route path="/online-tuition-qatar" element={<OnlineCountry countrySlug="qatar" />} />
                            <Route path="/online-tuition-kuwait" element={<OnlineCountry countrySlug="kuwait" />} />
                            <Route path="/online-tuition-oman" element={<OnlineCountry countrySlug="oman" />} />
                            <Route path="/online-tuition-bahrain" element={<OnlineCountry countrySlug="bahrain" />} />

                            {/* V61 — 14 new priority countries (France, Netherlands, Ireland, Switzerland,
                                Norway, Sweden, Finland, Denmark, Belgium, Italy, Spain, Japan, South Korea, Malaysia)
                                for global online tuition dominance. */}
                            <Route path="/online-tuition-france" element={<OnlineCountry countrySlug="france" />} />
                            <Route path="/online-tuition-netherlands" element={<OnlineCountry countrySlug="netherlands" />} />
                            <Route path="/online-tuition-ireland" element={<OnlineCountry countrySlug="ireland" />} />
                            <Route path="/online-tuition-switzerland" element={<OnlineCountry countrySlug="switzerland" />} />
                            <Route path="/online-tuition-norway" element={<OnlineCountry countrySlug="norway" />} />
                            <Route path="/online-tuition-sweden" element={<OnlineCountry countrySlug="sweden" />} />
                            <Route path="/online-tuition-finland" element={<OnlineCountry countrySlug="finland" />} />
                            <Route path="/online-tuition-denmark" element={<OnlineCountry countrySlug="denmark" />} />
                            <Route path="/online-tuition-belgium" element={<OnlineCountry countrySlug="belgium" />} />
                            <Route path="/online-tuition-italy" element={<OnlineCountry countrySlug="italy" />} />
                            <Route path="/online-tuition-spain" element={<OnlineCountry countrySlug="spain" />} />
                            <Route path="/online-tuition-japan" element={<OnlineCountry countrySlug="japan" />} />
                            <Route path="/online-tuition-south-korea" element={<OnlineCountry countrySlug="south-korea" />} />
                            <Route path="/online-tuition-malaysia" element={<OnlineCountry countrySlug="malaysia" />} />

                            {/* V95 — 15 additional country hubs for previously-uncovered markets.
                                Additive; every existing country route above is untouched. */}
                            <Route path="/online-tuition-portugal" element={<OnlineCountry countrySlug="portugal" />} />
                            <Route path="/online-tuition-austria" element={<OnlineCountry countrySlug="austria" />} />
                            <Route path="/online-tuition-poland" element={<OnlineCountry countrySlug="poland" />} />
                            <Route path="/online-tuition-czech-republic" element={<OnlineCountry countrySlug="czech-republic" />} />
                            <Route path="/online-tuition-hungary" element={<OnlineCountry countrySlug="hungary" />} />
                            <Route path="/online-tuition-romania" element={<OnlineCountry countrySlug="romania" />} />
                            <Route path="/online-tuition-turkey" element={<OnlineCountry countrySlug="turkey" />} />
                            <Route path="/online-tuition-israel" element={<OnlineCountry countrySlug="israel" />} />
                            <Route path="/online-tuition-hong-kong" element={<OnlineCountry countrySlug="hong-kong" />} />
                            <Route path="/online-tuition-thailand" element={<OnlineCountry countrySlug="thailand" />} />
                            <Route path="/online-tuition-indonesia" element={<OnlineCountry countrySlug="indonesia" />} />
                            <Route path="/online-tuition-philippines" element={<OnlineCountry countrySlug="philippines" />} />
                            <Route path="/online-tuition-south-africa" element={<OnlineCountry countrySlug="south-africa" />} />
                            <Route path="/online-tuition-brazil" element={<OnlineCountry countrySlug="brazil" />} />
                            <Route path="/online-tuition-mexico" element={<OnlineCountry countrySlug="mexico" />} />

                            {/* V61 — Global "1:1 Online Tuition Worldwide" hub */}
                            <Route path="/online-tuition-worldwide" element={<OnlineTuitionWorldwide />} />

                            {/* V61 Phase 3 — Offline hyperlocal locality Home Tuition pages
                                (STRICTLY Jabalpur / Bhopal / Nagpur only — the ONLY cities
                                where in-home tutor visits are legally offered).
                                URL pattern: /home-tuition-{locality}-{city}
                                10 localities × 3 cities = 30 pages. */}
                            {Object.entries(OFFLINE_LOCALITIES).flatMap(([citySlug, localities]) =>
                                localities.map((loc) => (
                                    <Route
                                        key={`${loc.slug}-${citySlug}`}
                                        path={`/home-tuition-${loc.slug}-${citySlug}`}
                                        element={<HomeTuitionLocality localitySlug={loc.slug} citySlug={citySlug} />}
                                    />
                                ))
                            )}

                            {/* Tutor Jobs SEO landing pages — JobPosting schema, hiring-side */}
                            {TUTOR_JOB_SLUGS.map((slug) => (
                                <Route
                                    key={slug}
                                    path={`/${slug}`}
                                    element={<TutorJobLanding slug={slug} />}
                                />
                            ))}
                            {/* Convenient alias referenced from related-link blocks */}
                            <Route path="/apply-as-home-tutor" element={<BecomeATutor />} />

                            {/* V77 — 1-on-1 Live Online Makeover Classes (additive) */}
                            <Route path="/online-makeover-classes" element={<MakeoverLanding variant="master" />} />
                            {["usa", "canada", "uk", "australia", "nz", "europe", "uae", "saudi-arabia", "qatar", "kuwait", "oman", "bahrain", "singapore", "malaysia", "india"].map((cs) => (
                                <Route key={cs} path={`/online-makeover-classes-${cs}`} element={<MakeoverLanding variant="country" countrySlug={cs} />} />
                            ))}
                            {["personal-grooming", "makeup-training", "skincare-guidance", "personal-styling", "wardrobe-consultation", "hair-styling-guidance", "image-consulting", "personal-branding", "confidence-personality", "professional-appearance-coaching"].map((cs) => (
                                <Route key={cs} path={`/online-makeover-classes/${cs}`} element={<MakeoverLanding variant="category" categorySlug={cs} />} />
                            ))}

                            {/* V96 — Dedicated 1:1 Offline Makeover landing for Jabalpur (SEO only,
                                NOT wired into homepage / navigation / menus). Additive; existing
                                Worldwide Live Online Makeover Classes routes above untouched. */}
                            <Route path="/makeover-classes-jabalpur" element={<MakeoverJabalpurOffline />} />

                            {/* V61 Phase 2 — Programmatic Board × Subject × City SEO
                                6 boards × 12 subjects × 20 cities = 1,440 pages.
                                MUST be registered LAST (just above catch-all) so all static
                                routes above take precedence. Unmatched slugs fall to NotFound. */}
                            <Route path="/:progSlug" element={<ProgrammaticBoardSubjectCityRoute />} />

                            <Route path="*" element={<NotFound />} />
                        </Route>

                        {/* Admin (no main layout) */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                        <Route
                            path="/admin/dashboard"
                            element={
                                <ProtectedAdminRoute>
                                    <AdminDashboard />
                                </ProtectedAdminRoute>
                            }
                        />
                        {/* V75 — SEO Audit Dashboard (Phase 5 Continuous SEO) */}
                        <Route
                            path="/admin/seo-audit"
                            element={
                                <ProtectedAdminRoute>
                                    <SeoAuditDashboard />
                                </ProtectedAdminRoute>
                            }
                        />
                        {/* V159 — HMS Key Wizard (Live Classroom setup) */}
                        <Route
                            path="/admin/live-classroom-setup"
                            element={
                                <ProtectedAdminRoute>
                                    <LiveClassroomSetup />
                                </ProtectedAdminRoute>
                            }
                        />
                        {/* V77 — Makeover Class Leads (admin) */}
                        <Route
                            path="/admin/makeover-leads"
                            element={
                                <ProtectedAdminRoute>
                                    <MakeoverLeadsPanel />
                                </ProtectedAdminRoute>
                            }
                        />

                        {/* V25 — Tutor Portal (gated by app_settings.tutor_portal_enabled) */}
                        <Route path="/tutor/login" element={<TutorLogin />} />
                        <Route path="/tutor/unavailable" element={<TutorPortalUnavailable />} />
                        <Route
                            path="/tutor/dashboard"
                            element={
                                <ProtectedTutorRoute>
                                    <TutorDashboard />
                                </ProtectedTutorRoute>
                            }
                        />
                        <Route
                            path="/tutor/profile"
                            element={
                                <ProtectedTutorRoute>
                                    <TutorProfile />
                                </ProtectedTutorRoute>
                            }
                        />
                        <Route
                            path="/tutor/leads"
                            element={
                                <ProtectedTutorRoute>
                                    <TutorLeads />
                                </ProtectedTutorRoute>
                            }
                        />
                        <Route path="/tutor/demos" element={<ProtectedTutorRoute><TutorDemos /></ProtectedTutorRoute>} />
                        <Route path="/tutor/tuitions" element={<ProtectedTutorRoute><TutorTuitions /></ProtectedTutorRoute>} />
                        <Route path="/tutor/tuitions/:id/attendance" element={<ProtectedTutorRoute><TutorAttendance /></ProtectedTutorRoute>} />
                        <Route path="/tutor/calendar" element={<ProtectedTutorRoute><TutorCalendar /></ProtectedTutorRoute>} />
                        <Route path="/tutor/notifications" element={<ProtectedTutorRoute><TutorNotifications /></ProtectedTutorRoute>} />
                        <Route path="/tutor/support" element={<ProtectedTutorRoute><TutorSupport /></ProtectedTutorRoute>} />
                        <Route path="/tutor/earnings" element={<ProtectedTutorRoute><TutorEarnings /></ProtectedTutorRoute>} />
                        <Route path="/tutor/refer" element={<ProtectedTutorRoute><TutorRefer /></ProtectedTutorRoute>} />
                        <Route path="/tutor/me" element={<ProtectedTutorRoute><TutorMe /></ProtectedTutorRoute>} />
                        <Route path="/tutor/faqs" element={<ProtectedTutorRoute><TutorFAQs /></ProtectedTutorRoute>} />
                    </Routes>
                        </SettingsProvider>
                        </TutorAuthProvider>
                    </AuthProvider>
                </BrowserRouter>
            </HelmetProvider>
        </div>
        </ErrorBoundary>
    );
}

export default App;
